import * as React from 'react';
import NavBar from './NavBar';
import { useParams } from 'react-router-dom';

export default function TvShowVideoPlayer() {
    let { tvShowName, season, episode } = useParams();
    const [width, setWidth] = React.useState(window.innerWidth);
    const [height, setHeight] = React.useState(window.innerHeight);
    const [castSession, setCastSession] = React.useState(null);
    const [isCastAvailable, setIsCastAvailable] = React.useState(false);
    const [isConnected, setIsConnected] = React.useState(false);
    const [castSDKLoaded, setCastSDKLoaded] = React.useState(false);
    const [castError, setCastError] = React.useState(null);
    const [showControls, setShowControls] = React.useState(false);
    const videoRef = React.useRef(null);
    const videoContainerRef = React.useRef(null);

    React.useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    React.useEffect(() => {
        const loadCastSDK = () => {
            return new Promise((resolve, reject) => {
                // Check if already loaded
                if (window.chrome && window.chrome.cast && window.chrome.cast.isAvailable) {
                    console.log('Cast SDK already loaded');
                    resolve();
                    return;
                }

                // Check if we're in a supported browser
                if (!window.chrome) {
                    reject('Cast not supported - Chromium browser required');
                    return;
                }

                console.log('Loading Cast SDK...');
                
                // Load the SDK
                const script = document.createElement('script');
                script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
                
                script.onload = () => {
                    console.log('Cast SDK script loaded, waiting for framework...');
                    
                    // Wait for cast framework to be ready with timeout
                    let attempts = 0;
                    const maxAttempts = 100; // 10 seconds max
                    
                    const checkCast = setInterval(() => {
                        attempts++;
                        
                        if (window.chrome && window.chrome.cast && window.chrome.cast.isAvailable) {
                            clearInterval(checkCast);
                            console.log('Cast framework ready after', attempts * 100, 'ms');
                            resolve();
                        } else if (attempts >= maxAttempts) {
                            clearInterval(checkCast);
                            reject('Cast SDK failed to initialize within timeout');
                        }
                    }, 100);
                };
                
                script.onerror = (error) => {
                    console.error('Failed to load Cast SDK script:', error);
                    reject('Failed to load Cast SDK script');
                };
                
                document.head.appendChild(script);
            });
        };

        const initializeCast = () => {
            if (!window.chrome || !window.chrome.cast) {
                console.log('Cast SDK not available for initialization');
                return;
            }

            const cast = window.chrome.cast;
            const sessionRequest = new cast.SessionRequest(cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID);
            const apiConfig = new cast.ApiConfig(
                sessionRequest,
                sessionListener,
                receiverListener,
                cast.AutoJoinPolicy.ORIGIN_SCOPED
            );

            cast.initialize(apiConfig, onInitSuccess, onInitError);
        };

        const onInitSuccess = () => {
            console.log('Cast SDK initialized successfully');
            setCastSDKLoaded(true);
            setCastError(null);
        };

        const onInitError = (error) => {
            console.log('Cast SDK initialization error:', error);
            setCastError('Cast initialization failed');
            setCastSDKLoaded(false);
        };

        const sessionListener = (session) => {
            console.log('New cast session:', session);
            setCastSession(session);
            setIsConnected(true);
            session.addUpdateListener(sessionUpdateListener);
        };

        const sessionUpdateListener = (isAlive) => {
            if (!isAlive) {
                setCastSession(null);
                setIsConnected(false);
            }
        };

        const receiverListener = (availability) => {
            const isAvailable = availability === window.chrome?.cast?.ReceiverAvailability?.AVAILABLE;
            console.log('Cast receiver availability:', isAvailable);
            setIsCastAvailable(isAvailable);
        };

        // Start the loading process
        loadCastSDK()
            .then(() => {
                console.log('Cast SDK loaded successfully, initializing...');
                initializeCast();
            })
            .catch((error) => {
                console.log('Cast SDK load failed:', error);
                setCastError(error);
                setCastSDKLoaded(false);
                setIsCastAvailable(false);
            });
    }, []);

    const startCasting = () => {
        if (!window.chrome || !window.chrome.cast) {
            alert('Google Cast is not available');
            return;
        }

        const cast = window.chrome.cast;
        cast.requestSession(
            (session) => {
                console.log('Cast session started:', session);
                setCastSession(session);
                setIsConnected(true);
                session.addUpdateListener((isAlive) => {
                    if (!isAlive) {
                        setCastSession(null);
                        setIsConnected(false);
                    }
                });
                
                // Load media
                loadMedia(session);
            },
            (error) => {
                console.log('Error starting cast session:', error);
            }
        );
    };

    const loadMedia = (session) => {
        if (!session) return;

        // Clean up episode name for URL
        const cleanEpisode = episode.replace(/\s/g, "%20");
        const videoUrl = `https://d3hdybpgwjnj3g.cloudfront.net/TvShows/${tvShowName}/${season}/${cleanEpisode}.mp4`;
        const mediaInfo = new window.chrome.cast.media.MediaInfo(videoUrl, 'video/mp4');
        
        // Set metadata with TV show information
        mediaInfo.metadata = new window.chrome.cast.media.TvShowMediaMetadata();
        mediaInfo.metadata.title = `${tvShowName} - ${season} ${episode}`;
        mediaInfo.metadata.seriesTitle = tvShowName;
        mediaInfo.metadata.season = parseInt(season.replace('s', ''));
        mediaInfo.metadata.episode = parseInt(episode.replace(/\D/g, ''));
        
        const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
        
        // Get current time from local video if available
        if (videoRef.current && !videoRef.current.paused) {
            request.currentTime = videoRef.current.currentTime;
            videoRef.current.pause(); // Pause local video
        }

        session.loadMedia(request).then(
            () => {
                console.log('TV show episode loaded successfully');
            },
            (error) => {
                console.log('Error loading TV show episode:', error);
            }
        );
    };

    const stopCasting = () => {
        if (castSession) {
            castSession.stop(
                () => {
                    console.log('Cast session stopped');
                    setCastSession(null);
                    setIsConnected(false);
                },
                (error) => {
                    console.log('Error stopping cast session:', error);
                }
            );
        }
    };

    const playPause = () => {
        if (castSession && castSession.getMediaSession()) {
            const mediaSession = castSession.getMediaSession();
            if (mediaSession.playerState === window.chrome.cast.media.PlayerState.PLAYING) {
                mediaSession.pause(new window.chrome.cast.media.PauseRequest());
            } else {
                mediaSession.play(new window.chrome.cast.media.PlayRequest());
            }
        }
    };

    const CastOverlay = () => {
        // Don't show anything if Cast SDK failed to load or not available
        if (castError || !castSDKLoaded || !isCastAvailable) return null;

        return (
            <div
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    opacity: showControls ? 1 : 0,
                    visibility: showControls ? 'visible' : 'hidden',
                    transition: 'opacity 0.3s ease, visibility 0.3s ease',
                    zIndex: 10,
                    pointerEvents: showControls ? 'auto' : 'none'
                }}
            >
                {!isConnected ? (
                    <button
                        onClick={startCasting}
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            border: 'none',
                            padding: '8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px'
                        }}
                        title="Cast to TV"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M1,18 L1,21 L4,21 L4,18 L1,18 Z M5,18 L5,21 L8,21 L8,18 L5,18 Z M9,18 L9,21 L12,21 L12,18 L9,18 Z M13,18 L13,21 L16,21 L16,18 L13,18 Z M17,18 L17,21 L20,21 L20,18 L17,18 Z M21,18 L21,21 L24,21 L24,18 L21,18 Z M1,14 L1,17 L4,17 L4,14 L1,14 Z M21,14 L21,17 L24,17 L24,14 L21,14 Z M1,10 L1,13 L4,13 L4,10 L1,10 Z M21,10 L21,13 L24,13 L24,10 L21,10 Z M1,6 L1,9 L4,9 L4,6 L1,6 Z M21,6 L21,9 L24,9 L24,6 L21,6 Z M1,2 L1,5 L4,5 L4,2 L1,2 Z M5,2 L5,5 L8,5 L8,2 L5,2 Z M9,2 L9,5 L12,5 L12,2 L9,2 Z M13,2 L13,5 L16,5 L16,2 L13,2 Z M17,2 L17,5 L20,5 L20,2 L17,2 Z M21,2 L21,5 L24,5 L24,2 L21,2 Z" />
                        </svg>
                    </button>
                ) : (
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                            onClick={playPause}
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                border: 'none',
                                padding: '6px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                            title="Play/Pause Cast"
                        >
                            ⏯️
                        </button>
                        <button
                            onClick={stopCasting}
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                border: 'none',
                                padding: '6px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                            title="Stop Casting"
                        >
                            ⏹️
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Clean up episode name for URL
    const cleanEpisode = episode.replace(/\s/g, "%20");

    return (
        <>
            <NavBar />
            <div>
                <div 
                    ref={videoContainerRef}
                    style={{ position: 'relative', display: 'inline-block' }}
                    onMouseEnter={() => setShowControls(true)}
                    onMouseLeave={() => setShowControls(false)}
                >
                    <CastOverlay />
                    <video 
                        ref={videoRef}
                        id="video_element" 
                        controls 
                        autoPlay 
                        style={{
                            maxHeight: height, 
                            maxWidth: width,
                            display: isConnected ? 'none' : 'block'
                        }}
                    >
                        <source src={`https://d3hdybpgwjnj3g.cloudfront.net/TvShows/${tvShowName}/${season}/${cleanEpisode}.mp4`} type="video/mp4"/>
                    </video>
                </div>
                {isConnected && (
                    <div style={{
                        textAlign: 'center',
                        padding: '50px',
                        fontSize: '18px',
                        color: '#666'
                    }}>
                        <p>Casting to your TV</p>
                        <p>Use the controls above to manage playback</p>
                    </div>
                )}
            </div>
        </>
    );
}